import React, { useMemo, useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { getFunctions } from 'firebase/functions';

// Helper to build an ICS calendar invite
function buildICS({ uid, startISO, endISO, summary, description, url, attendees = [], organizer = 'ASD Flo <noreply@asdflo.com>' }) {
  const dtStart = startISO.replace(/[-:]/g, '').split('.')[0] + 'Z';
  const dtEnd = endISO.replace(/[-:]/g, '').split('.')[0] + 'Z';
  const dtStamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ASDflo//Meeting Scheduler//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description || ''}\\nJoin: ${url}`,
    `URL:${url}`,
  ];
  if (organizer) lines.push(`ORGANIZER:${organizer}`);
  attendees.forEach(a => lines.push(`ATTENDEE:${a}`));
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

export default function ScheduleMeeting() {
  const [subject, setSubject] = useState('Video Consultation');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const functions = useMemo(() => getFunctions(), []);

  const createUrl = process.env.REACT_APP_CREATE_MEETING_URL || 'https://us-central1-asdflobeta.cloudfunctions.net/createVideoMeetingHttp';
  // Always use a public base URL for share links (never localhost)
  const publicBaseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || 'https://www.asdflo.com';

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !p1 || !p2 || !date || !startTime || !endTime) {
      alert('Please fill all fields');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      // 1) Create roomId via backend
      const res = await fetch(createUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (!res.ok || !data.roomId) throw new Error(data.error || 'Failed to create room');
      const roomId = data.roomId;

      // 2) Build schedule times in ISO
      const startISO = new Date(`${date}T${startTime}:00`).toISOString();
      const endISO = new Date(`${date}T${endTime}:00`).toISOString();

      // 3) Persist meeting document with clean URL id = roomId
      const docRef = doc(db, 'meetings', roomId);
      const meetingDoc = {
        id: roomId,
        subject,
        participants: [p1.trim().toLowerCase(), p2.trim().toLowerCase()],
        startAt: startISO,
        endAt: endISO,
        status: 'scheduled', // scheduled | active | cancelled | completed
        transcriptPath: '',
        summary: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(docRef, meetingDoc);

      // 4) Compose meeting link: clean URL
      const meetingLink = `${publicBaseUrl}/meetings/${encodeURIComponent(roomId)}`;

      // 5) Send invites via server callable (branded template + magic links + ICS)
      const sendMeetingInvites = httpsCallable(functions, 'sendMeetingInvites');
      await sendMeetingInvites({
        meetingId: roomId,
        subject,
        p1: p1.trim().toLowerCase(),
        p2: p2.trim().toLowerCase(),
        startISO,
        endISO,
        baseUrl: publicBaseUrl
      });

      setResult({ roomId, meetingLink });
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-4">Schedule Meeting</h1>
      <form onSubmit={onSubmit} className="space-y-4 bg-white rounded-lg border p-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input className="w-full border rounded px-3 py-2" value={subject} onChange={(e)=>setSubject(e.target.value)} placeholder="Video Consultation" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Participant 1 Email</label>
            <input className="w-full border rounded px-3 py-2" value={p1} onChange={(e)=>setP1(e.target.value)} placeholder="p1@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Participant 2 Email</label>
            <input className="w-full border rounded px-3 py-2" value={p2} onChange={(e)=>setP2(e.target.value)} placeholder="p2@example.com" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={date} onChange={(e)=>setDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
            <input type="time" className="w-full border rounded px-3 py-2" value={startTime} onChange={(e)=>setStartTime(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
            <input type="time" className="w-full border rounded px-3 py-2" value={endTime} onChange={(e)=>setEndTime(e.target.value)} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={loading} className="px-5 py-2.5 rounded bg-blue-600 text-white disabled:opacity-60">
            {loading ? 'Scheduling…' : 'Schedule Meeting'}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
          <div className="font-medium text-green-800">Meeting scheduled</div>
          <div className="text-sm">ID: <code>{result.roomId}</code></div>
          <div className="flex items-center gap-3 text-sm mt-1 flex-wrap">
            <span>Link:</span>
            <a className="text-green-700 underline break-all" href={result.meetingLink} target="_blank" rel="noreferrer">{result.meetingLink}</a>
            <button
              type="button"
              onClick={async () => { try { await navigator.clipboard.writeText(result.meetingLink); setCopied(true); setTimeout(()=>setCopied(false), 2000);} catch(_){} }}
              className="px-3 py-1.5 rounded bg-green-600 text-white text-xs"
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
